import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { Shield, Crown, Users, Mail, Phone, MapPin, Calendar, Search, Send, MessageSquare } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import confetti from 'canvas-confetti';

const ADMIN_EMAILS = ['davidpolonsky@gmail.com'];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, free, trial, paid, vip

  // Check if user is admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by creation date (newest first)
      usersData.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });

      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPremiumStatus = (user) => {
    const prefs = user.preferences || {};
    if (prefs.premiumType === 'vip') return 'VIP';
    if (prefs.premiumType === 'trial') return 'Trial';
    if (prefs.premiumType === 'paid') return 'Paid';
    if (prefs.isPremium) return 'Premium'; // Legacy
    return 'Free';
  };

  const getPremiumBadgeColor = (status) => {
    switch(status) {
      case 'VIP': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Paid': return 'bg-green-100 text-green-800 border-green-300';
      case 'Trial': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Premium': return 'bg-amber-100 text-amber-800 border-amber-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const toggleVIPStatus = async (userId, currentStatus, userEmail) => {
    const isVIP = currentStatus === 'VIP';

    try {
      await updateDoc(doc(db, 'users', userId), {
        'preferences.isPremium': !isVIP ? true : false,
        'preferences.premiumType': !isVIP ? 'vip' : null,
        'preferences.vipGrantedBy': !isVIP ? user.email : null,
        'preferences.vipGrantedAt': !isVIP ? new Date().toISOString() : null,
        updatedAt: new Date().toISOString()
      });

      // Send VIP welcome email
      if (!isVIP && userEmail) {
        try {
          const response = await fetch('https://us-central1-outtaten.cloudfunctions.net/sendVIPWelcomeEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: userEmail,
              grantedBy: user.email
            })
          });

          if (response.ok) {
            toast.success('📧 VIP welcome email sent!', { duration: 2000 });
          }
        } catch (emailError) {
          console.error('Error sending VIP welcome email:', emailError);
          // Don't block the VIP grant if email fails
        }
      }

      // Refresh users list
      loadUsers();

      // Celebrate with confetti!
      if (!isVIP) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#9370DB']
        });
        toast.success('👑 VIP ACCESS GRANTED!', {
          duration: 4000,
          style: {
            background: 'linear-gradient(135deg, #9333ea 0%, #c026d3 100%)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
          }
        });
      }
    } catch (error) {
      console.error('Error updating VIP status:', error);
      toast.error('Failed to update VIP status');
    }
  };

  const beachEmojis = ['🏖️', '🌊', '☀️', '🏄‍♀️', '🌴', '🍹'];

  const sendTestEmail = async (email) => {
    const loadingToast = toast.loading('📧 Sending email...');

    try {
      const response = await fetch('https://us-central1-outtaten.cloudfunctions.net/sendTestEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, adminEmail: user.email })
      });

      const result = await response.json();
      toast.dismiss(loadingToast);

      if (result.success) {
        const randomEmoji = beachEmojis[Math.floor(Math.random() * beachEmojis.length)];
        toast.success(`${randomEmoji} Email sent to ${email.split('@')[0]}!`, {
          duration: 3000,
          style: {
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            color: 'white',
            fontWeight: 'bold',
          }
        });
      } else {
        toast.error(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    }
  };

  const sendTestSMS = async (phoneNumber) => {
    if (!phoneNumber) {
      toast.error('User has no phone number set');
      return;
    }

    const loadingToast = toast.loading('📱 Sending SMS...');

    try {
      const response = await fetch('https://us-central1-outtaten.cloudfunctions.net/sendTestSMS', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber, adminEmail: user.email })
      });

      const result = await response.json();
      toast.dismiss(loadingToast);

      if (result.success) {
        const randomEmoji = beachEmojis[Math.floor(Math.random() * beachEmojis.length)];
        toast.success(`${randomEmoji} SMS sent to ${phoneNumber.slice(-4)}!`, {
          duration: 3000,
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            color: 'white',
            fontWeight: 'bold',
          }
        });
      } else {
        toast.error(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error sending test SMS:', error);
      toast.error('Failed to send test SMS');
    }
  };

  // Calculate login stats
  const getLoginStats = (user) => {
    const loginHistory = user.loginHistory || [];
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const thisWeekLogins = loginHistory.filter(loginDate =>
      new Date(loginDate) >= oneWeekAgo
    ).length;

    return {
      allTime: loginHistory.length,
      thisWeek: thisWeekLogins,
      lastLogin: user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'
    };
  };

  const filteredUsers = users.filter(u => {
    const status = getPremiumStatus(u);
    const matchesSearch =
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.preferences?.homeZipCode?.includes(searchTerm);

    const matchesFilter =
      filterStatus === 'all' ||
      status.toLowerCase() === filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: users.length,
    free: users.filter(u => getPremiumStatus(u) === 'Free').length,
    trial: users.filter(u => getPremiumStatus(u) === 'Trial').length,
    paid: users.filter(u => getPremiumStatus(u) === 'Paid').length,
    vip: users.filter(u => getPremiumStatus(u) === 'VIP').length
  };

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
        <p className="text-white/80">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto text-center py-20">
        <div className="text-white text-xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <Toaster position="bottom-right" />
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-xl mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-gray-600 text-sm">Total Users</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-gray-600 text-sm">Free</div>
            <div className="text-2xl font-bold text-gray-800">{stats.free}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-blue-600 text-sm">Trial</div>
            <div className="text-2xl font-bold text-blue-800">{stats.trial}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-green-600 text-sm">Paid</div>
            <div className="text-2xl font-bold text-green-800">{stats.paid}</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-purple-600 text-sm">VIP</div>
            <div className="text-2xl font-bold text-purple-800">{stats.vip}</div>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="mb-4">
          <button
            onClick={async () => {
              if (!window.confirm('Send NPS survey to ALL users?')) return;

              toast.loading('Sending NPS survey...', { id: 'nps' });

              try {
                const response = await fetch('https://us-central1-outtaten.cloudfunctions.net/sendNPSSurvey', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ adminEmail: user.email })
                });

                const data = await response.json();

                if (data.success) {
                  toast.success(`NPS survey queued for ${data.emails.length} users!`, { id: 'nps', duration: 5000 });
                  confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 }
                  });
                } else {
                  throw new Error(data.error || 'Failed to send');
                }
              } catch (error) {
                toast.error(`Failed: ${error.message}`, { id: 'nps' });
              }
            }}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <MessageSquare className="w-5 h-5" />
            Send NPS Survey to All Users
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email, name, or ZIP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-400 focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'free', 'trial', 'paid', 'vip'].map(filter => (
              <button
                key={filter}
                onClick={() => setFilterStatus(filter)}
                className={`px-4 py-2 rounded-lg font-semibold capitalize transition-all ${
                  filterStatus === filter
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Logins</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Locations</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Alerts</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Joined</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Test Alerts</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">VIP Access</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((userData) => {
                const status = getPremiumStatus(userData);
                const prefs = userData.preferences || {};

                // Handle both old (homeZipCode) and new (homeLocation object) formats
                const homeLocationDisplay = prefs.homeLocation
                  ? (prefs.homeLocation.shortName || prefs.homeLocation.displayName || prefs.homeLocation.name)
                  : prefs.homeZipCode;

                const additionalLocationDisplays = (prefs.additionalLocations || [])
                  .filter(Boolean)
                  .map(loc => typeof loc === 'string' ? loc : (loc.shortName || loc.displayName || loc.name));

                const locations = [homeLocationDisplay, ...additionalLocationDisplays].filter(Boolean);

                return (
                  <tr key={userData.id} className="hover:bg-gray-50 transition-colors">
                    {/* User Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-gray-400 mt-1" />
                        <div>
                          <div className="font-semibold text-gray-800">{userData.displayName || 'No name'}</div>
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {userData.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-semibold ${getPremiumBadgeColor(status)}`}>
                        {status === 'VIP' && <Crown className="w-3 h-3" />}
                        {status}
                      </span>
                    </td>

                    {/* Login Stats */}
                    <td className="px-6 py-4">
                      {(() => {
                        const loginStats = getLoginStats(userData);
                        return (
                          <div className="text-sm">
                            <div className="font-semibold text-gray-800">
                              {loginStats.allTime} total
                            </div>
                            <div className="text-xs text-gray-600">
                              {loginStats.thisWeek} this week
                            </div>
                            <div className="text-xs text-gray-400">
                              Last: {loginStats.lastLogin}
                            </div>
                          </div>
                        );
                      })()}
                    </td>

                    {/* Locations */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {locations.length > 0 ? locations.join(', ') : 'None'}
                      </div>
                    </td>

                    {/* Alerts */}
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-xs">
                        {prefs.emailEnabled && (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Mail className="w-3 h-3" />
                            Email
                          </div>
                        )}
                        {prefs.smsEnabled && (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Phone className="w-3 h-3" />
                            SMS: {prefs.phoneNumber}
                          </div>
                        )}
                        {!prefs.emailEnabled && !prefs.smsEnabled && (
                          <div className="text-gray-400">None</div>
                        )}
                      </div>
                    </td>

                    {/* Joined Date */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        {userData.createdAt
                          ? new Date(userData.createdAt).toLocaleDateString()
                          : 'Unknown'
                        }
                      </div>
                    </td>

                    {/* Test Alerts */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => sendTestEmail(userData.email)}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                          title="Send test email"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => sendTestSMS(prefs.phoneNumber)}
                          disabled={!prefs.phoneNumber}
                          className={`p-2 rounded-lg transition-colors ${
                            prefs.phoneNumber
                              ? 'bg-green-100 hover:bg-green-200 text-green-700'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                          title={prefs.phoneNumber ? 'Send test SMS' : 'No phone number'}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      </div>
                    </td>

                    {/* VIP Toggle */}
                    <td className="px-6 py-4 text-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={status === 'VIP'}
                          onChange={() => toggleVIPStatus(userData.id, status, userData.email)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          {status === 'VIP' ? 'Revoke' : 'Grant'}
                        </span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No users found matching your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
